import _ from 'lodash';
import { getInstance } from '@snapshot-labs/lock/plugins/vue3';
import {
  getErc20Contract,
  getMinterAprWeights,
  getMinterContract,
  getMinterTvlLevels,
  getPortfolioAndPairRegistryContract,
  getStakingContract,
  transactionWithEstimatedGas,
} from '@/helpers/contract.helper';
import { BN_ZERO, ethersToJSBI, SOLIDITY_TYPE_MAXIMA, SolidityType } from '@/sdk/constants';
import { BOT_USD_DECIMALS } from '@/helpers/decimals-env';
import JSBI from 'jsbi';
import { Staking } from '@/sdk/entities/staking';
import { parseUnits } from '@ethersproject/units';
import { AUTO_STAKING_MUTATION_TYPES } from '@/store/modules/staking/autoStaking.module';
import { fromWei } from '@/sdk/utils';
import { dateNowInSeconds } from '@/helpers/utils';
import { fetchPricesInUSDByAddresses } from '@/helpers/backend-api';
import { useEVMWallet } from '@/store/modules/wallet/useEVMWallet';
import { useTokens } from '@/store/modules/tokens/useTokens';

const LOGGER = {
  groupCollapsed: (...label: any[]) => {
    if (isLoggingDisabled()) return;

    console.groupCollapsed(...label);
  },
  groupEnd: () => {
    if (isLoggingDisabled()) return;

    console.groupEnd();
  },
  log: (message?: any, ...optionalParams: any[]) => {
    if (isLoggingDisabled()) return;

    console.log(message, ...optionalParams);
  },
};

export const STAKING_ACTION_TYPES = {
  STAKING_INIT: 'stakingInit',
  GET_STATUS_STAKING: 'getStatusStaking',
  GET_TVL_LEVELS: 'getTvlLevels',
  GET_APR_WEIGHTS: 'getAprWeights',
  UPDATE_STAKING_POOLS: 'updateStakingPools',
  ENABLE_STAKING: 'enableStaking',
  CHECK_ALLOWANCE: 'checkAllowance',
  STAKE_LQF: 'stakeLqf',
  UNSTAKE_LQF: 'unStakeLqf',
  STAKING_HARVEST: 'stakingHarvest',
  STAKING_COMPOUND: 'stakingCompound',
};

export const STAKING_MUTATION_TYPES = {
  SET_STATE: 'setState',
  ADD_POOL: 'addPool',
};

const state = {
  isStoreReady: false,
  hasError: false,
  statusStaking: [],
  pool: {},
  tvlLevels: [],
  aprWeights: [],
};
const getters = {
  getStakingTableData: state => {
    console.log('[MANUAL:POOLS] state.pool', state.pool);
    const tableDataArray = Object.values(state.pool || {});
    if (tableDataArray.length > 0) {
      return _.sortBy(tableDataArray, ['isSyrupPool', 'asc']);
    }
    return [];
  },
};
const actions = {
  async [STAKING_ACTION_TYPES.STAKING_INIT]({ commit, dispatch, getters }) {
    commit(STAKING_MUTATION_TYPES.SET_STATE, {
      isStoreReady: false,
      hasError: false,
      statusStaking: [],
      pool: {},
    });
    try {
      dispatch(STAKING_ACTION_TYPES.GET_TVL_LEVELS);
      dispatch(STAKING_ACTION_TYPES.GET_APR_WEIGHTS);
      await dispatch(STAKING_ACTION_TYPES.GET_STATUS_STAKING);
      await dispatch(STAKING_ACTION_TYPES.UPDATE_STAKING_POOLS);
      const isReceivedStakingData = getters.getStakingTableData.length !== 0;
      commit(STAKING_MUTATION_TYPES.SET_STATE, { isStoreReady: isReceivedStakingData });
      if (!isReceivedStakingData) {
        dispatch(STAKING_ACTION_TYPES.STAKING_INIT);
      }
    } catch (e) {
      console.error('[STAKING] Happen error: ', e);
      commit(STAKING_MUTATION_TYPES.SET_STATE, { hasError: true });
    }
  },
  async [STAKING_ACTION_TYPES.GET_STATUS_STAKING]({ commit }) {
    LOGGER.log('STAKING_ACTION_TYPES.GET_STATUS_STAKING START');
    try {
      const minterContract = getMinterContract();
      const { walletState } = useEVMWallet();
      const statusStaking = await minterContract.getStatusStaking(
        walletState.account,
        dateNowInSeconds(),
      );
      LOGGER.log('statusStaking', statusStaking);
      commit(AUTO_STAKING_MUTATION_TYPES.SET_STATE, { statusStaking });
    } catch (e) {
      console.debug(e);
    }
    LOGGER.log('STAKING_ACTION_TYPES.GET_STATUS_STAKING END');
  },
  async [STAKING_ACTION_TYPES.GET_TVL_LEVELS]({ commit }) {
    try {
      const tvlLevels = await getMinterTvlLevels();
      commit(STAKING_MUTATION_TYPES.SET_STATE, { tvlLevels });
    } catch (e) {
      console.debug(e);
    }
  },
  async [STAKING_ACTION_TYPES.GET_APR_WEIGHTS]({ commit }) {
    try {
      const aprWeights = await getMinterAprWeights();
      commit(STAKING_MUTATION_TYPES.SET_STATE, { aprWeights });
    } catch (e) {
      console.debug(e);
    }
  },
  async [STAKING_ACTION_TYPES.UPDATE_STAKING_POOLS]({ state, commit }) {
    LOGGER.log('STAKING_ACTION_TYPES.UPDATE_STAKING_POOLS START');
    const { getBLUESToken } = useTokens();
    const nativeToken = getBLUESToken();

    await Promise.all(
      state.statusStaking.map(async statusStaking => {
        const profileContract = getPortfolioAndPairRegistryContract();
        const totalStaked = await profileContract.getManualYieldPoolTotalStaked();

        const tokenAddresses = [
          statusStaking.isSyrupPool ? statusStaking.tokenToMint : nativeToken.address,
        ];
        const prices = await fetchPricesInUSDByAddresses(tokenAddresses);
        LOGGER.log('prices : ', tokenAddresses, prices);

        const nativeTokenPriceInBase = fromWei(prices[0], BOT_USD_DECIMALS);
        LOGGER.log('nativeTokenPriceInBase', nativeTokenPriceInBase.toString());
        const newStaking = new Staking(
          statusStaking,
          nativeTokenPriceInBase,
          statusStaking.isSyrupPool ? undefined : totalStaked,
        );
        commit(STAKING_MUTATION_TYPES.ADD_POOL, newStaking);
      }),
    );
    LOGGER.log('STAKING_ACTION_TYPES.UPDATE_STAKING_POOLS END');
  },
  async [STAKING_ACTION_TYPES.CHECK_ALLOWANCE](_, { pool, amount }): Promise<void> {
    try {
      if (!getInstance()?.web3) {
        console.warn('[STAKING:CHECK:ALLOWANCE] Can not get signer.');
        return;
      }
      LOGGER.log('[STAKING:CHECK:ALLOWANCE]: pool: ', pool);

      const tokenContract = getErc20Contract(pool.token, getInstance().web3.getSigner());
      const { walletState } = useEVMWallet();
      const allowance = await tokenContract.allowance(walletState.account, pool.farm);
      const jsbiAllowance = ethersToJSBI(allowance);
      const jsbiAmount = ethersToJSBI(parseUnits(amount ?? '0'));

      const needApprove =
        JSBI.equal(jsbiAllowance, BN_ZERO) || JSBI.greaterThan(jsbiAmount, jsbiAllowance);
      LOGGER.groupCollapsed(
        `[STAKING:CHECK:ALLOWANCE] Need approve ${pool.token} : ${needApprove}`,
      );
      LOGGER.log(`${pool.token}     input: `, jsbiAmount.toString());
      LOGGER.log(`${pool.token} allowance: `, jsbiAllowance.toString());
      LOGGER.groupEnd();

      if (needApprove) {
        const approveRequest = await transactionWithEstimatedGas(tokenContract, 'approve', [
          pool.farm,
          SOLIDITY_TYPE_MAXIMA[SolidityType.uint256].toString(),
        ]);

        await approveRequest.wait();
      }
    } catch (ex) {
      console.debug('[STAKING:CHECK:ALLOWANCE:ERROR] Error: ', ex);
      throw ex;
    }
  },
  async [STAKING_ACTION_TYPES.STAKE_LQF](_, { pool, amount }) {
    LOGGER.log('STAKING_ACTION_TYPES.STAKE_LQF', pool, amount);
    try {
      const stakingContract = getStakingContract(pool.farm, getInstance().web3.getSigner());
      const bigIntAmount = parseUnits(amount).toString();
      const result = await transactionWithEstimatedGas(stakingContract, 'deposit', [bigIntAmount]);
      await result.wait().then(function (result) {
        Promise.resolve(result);
      });
    } catch (e) {
      console.debug(e);
      await Promise.reject(e);
    }
  },
  async [STAKING_ACTION_TYPES.UNSTAKE_LQF](_, { pool, amount }) {
    LOGGER.log('STAKING_ACTION_TYPES.UNSTAKE_LQF', pool, amount);
    try {
      const stakingContract = getStakingContract(pool.farm, getInstance().web3.getSigner());
      const bigIntAmount = parseUnits(amount).toString();
      const result = await transactionWithEstimatedGas(stakingContract, 'withdraw', [bigIntAmount]);
      await result.wait().then(function (result) {
        Promise.resolve(result);
      });
    } catch (e) {
      console.debug(e);
      await Promise.reject(e);
    }
  },
  async [STAKING_ACTION_TYPES.STAKING_COMPOUND](_, { pool }) {
    LOGGER.log('STAKING_ACTION_TYPES.STAKING_COMPOUND', pool);
    try {
      const stakingContract = getStakingContract(pool.farm, getInstance().web3.getSigner());
      const result = await transactionWithEstimatedGas(stakingContract, 'compound', []);
      await result.wait().then(function (result) {
        Promise.resolve(result);
      });
    } catch (e) {
      console.debug(e);
      await Promise.reject(e);
    }
  },
  async [STAKING_ACTION_TYPES.STAKING_HARVEST](_, { pool }) {
    LOGGER.log('STAKING_ACTION_TYPES.STAKING_HARVEST', pool);
    try {
      const stakingContract = getStakingContract(pool.farm, getInstance().web3.getSigner());
      const result = await transactionWithEstimatedGas(stakingContract, 'getReward', []);
      await result.wait().then(function (result) {
        Promise.resolve(result);
      });
    } catch (e) {
      console.debug(e);
      await Promise.reject(e);
    }
  },
};
const mutations = {
  [STAKING_MUTATION_TYPES.SET_STATE](_state, payload) {
    Object.keys(payload).forEach(key => {
      _state[key] = payload[key];
    });
  },
  [STAKING_MUTATION_TYPES.ADD_POOL](_state, payload) {
    _state.pool[payload.token] = payload;
  },
};

// DEBUG

function isLoggingDisabled() {
  return !window['BLUESHIFT_DEBUG'].MANUAL_POOLS;
}

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations,
};
